import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Plus, 
  Search, 
  Loader2, 
  User, 
  Phone, 
  Mail,
  MoreHorizontal,
  Pencil,
  UserX,
  UserCheck,
  Calendar,
  Trash2,
  FolderOpen,
  Upload,
  FileText,
  Image,
  X,
  Download,
  MapPin,
  CreditCard,
  ChevronLeft,
  RotateCcw,
  Eye,
  EyeOff,
  Key,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WorkerAvailabilityDialog } from '@/components/WorkerAvailabilityDialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Import worker dialogs from components
import { AddWorkerDialog, EditWorkerDialog } from '@/components/workers';

// Worker Row Actions
function WorkerActions({ worker, onUpdate, onOpenProfile, onEdit, currentUserRole }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);

  const isAdmin = currentUserRole === 'admin';
  // Manager can only edit plain workers
  const canEdit = isAdmin || (currentUserRole === 'manager' && worker.role === 'worker');

  const handleDeactivate = async () => {
    setLoading(true);
    try {
      await api.patch(`/workers/${worker.id}/activate?is_active=false`);
      toast.success(t('workers.deactivated'));
      onUpdate();
    } catch (err) {
      console.error('Failed to deactivate worker:', err);
      toast.error(t('workers.deactivateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    setLoading(true);
    try {
      await api.patch(`/workers/${worker.id}/activate?is_active=true`);
      toast.success(t('workers.restored'));
      onUpdate();
    } catch (err) {
      console.error('Failed to restore worker:', err);
      toast.error(t('workers.restoreFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" data-testid={`worker-actions-${worker.id}`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canEdit && (
            <DropdownMenuItem onClick={() => onEdit(worker)} data-testid={`worker-edit-${worker.id}`}>
              <Pencil className="mr-2 h-4 w-4" />
              {t('common.edit')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onOpenProfile(worker)} data-testid={`worker-profile-${worker.id}`}>
            <FolderOpen className="mr-2 h-4 w-4" />
            {t('workers.viewProfile')}
          </DropdownMenuItem>

          {isAdmin && (worker.is_active !== false ? (
            <DropdownMenuItem
              onClick={handleDeactivate}
              className="text-red-600 focus:text-red-600"
              data-testid={`worker-delete-${worker.id}`}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('workers.delete')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={handleRestore}
              className="text-green-600 focus:text-green-600"
              data-testid={`worker-restore-${worker.id}`}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('workers.restore')}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

// Worker Profile Sheet with Files
function WorkerProfileSheet({ worker, open, onOpenChange, onUpdate }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (open && worker) {
      fetchFiles();
      setShowPassword(false);
      setChangingPassword(false);
      setNewPassword('');
    }
  }, [open, worker]);

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error(t('workers.passwordTooShort'));
      return;
    }
    setSavingPassword(true);
    try {
      await api.patch(`/workers/${worker.id}/password`, { password: newPassword });
      toast.success(t('workers.passwordChanged'));
      setChangingPassword(false);
      setNewPassword('');
    } catch (err) {
      console.error('Failed to change password:', err);
      toast.error(t('workers.passwordChangeFailed'));
    } finally {
      setSavingPassword(false);
    }
  };

  const fetchFiles = async () => {
    if (!worker) return;
    setLoading(true);
    try {
      const response = await api.get(`/workers/${worker.id}/files`);
      setFiles(response.data);
    } catch (err) {
      console.error('Failed to fetch files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', file.type.startsWith('image/') ? 'photo' : 'document');

    try {
      await api.post(`/workers/${worker.id}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('workers.fileUploaded'));
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('workers.fileUploadError'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await api.delete(`/workers/${worker.id}/files/${fileId}`);
      toast.success(t('workers.fileDeleted'));
      fetchFiles();
    } catch (err) {
      toast.error(t('workers.fileDeleteError'));
    }
  };

  if (!worker) return null;

  const initials = `${worker.first_name?.[0] || ''}${worker.last_name?.[0] || ''}`.toUpperCase();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl"
              style={{ backgroundColor: worker.calendar_color || '#4F46E5' }}
            >
              {initials}
            </div>
            <div>
              <SheetTitle className="text-xl">{worker.first_name} {worker.last_name}</SheetTitle>
              <SheetDescription>{worker.specialization || t('workers.worker')}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Contact Info */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              {t('workers.contactInfo')}
            </h3>
            <div className="space-y-2">
              {worker.email && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/10 border border-accent/20">
                  <Mail className="h-5 w-5 text-accent" />
                  <span className="text-base">{worker.email}</span>
                </div>
              )}
              {worker.phone && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <span className="text-base">{worker.phone}</span>
                </div>
              )}
              {worker.home_address && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{worker.home_address}</span>
                </div>
              )}
              {worker.cin_number && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">CIN: {worker.cin_number}</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Info */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              {t('workers.salary')}
            </h3>
            {worker.payment_type === 'percentage' ? (
              <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                  {worker.percentage_rate || 0}%
                </p>
                <p className="text-sm text-purple-600 dark:text-purple-500 mt-1">
                  {t('workers.percentageFromRevenue')}
                </p>
              </div>
            ) : worker.hourly_rate ? (
              <div className={`p-4 rounded-lg border ${parseFloat(worker.hourly_rate) < 17.40 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'}`}>
                <p className={`text-2xl font-bold ${parseFloat(worker.hourly_rate) < 17.40 ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'}`}>
                  ${worker.hourly_rate} <span className="text-sm font-normal opacity-70">/ {t('time.hours')}</span>
                </p>
                {parseFloat(worker.hourly_rate) < 17.40 && (
                  <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">⚠️ Ниже минимальной ставки BC 2025 ($17.40/ч)</p>
                )}
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-muted/50 text-muted-foreground text-sm">
                {t('workers.noRateSet')}
              </div>
            )}
          </div>

          {/* Login Credentials */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              {t('workers.credentials')}
            </h3>
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900/50 border space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('workers.login')}</p>
                <p className="font-mono text-sm bg-background p-2 rounded border">{worker.email}</p>
              </div>
              
              {changingPassword ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{t('workers.newPassword')}</p>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('workers.enterNewPassword')}
                      className="flex-1 font-mono"
                      data-testid="worker-new-password"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleChangePassword}
                      disabled={savingPassword}
                      data-testid="worker-save-password"
                    >
                      {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => { setChangingPassword(false); setNewPassword(''); }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('workers.password')}</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm bg-background p-2 rounded border flex-1">
                      ••••••••
                    </p>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setChangingPassword(true)}
                      data-testid="worker-change-password"
                    >
                      {t('workers.resetPassword') || 'Сбросить пароль'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('workers.passwordSecurityNote') || 'Пароли не хранятся в открытом виде'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Files Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                {t('workers.files')}
              </h3>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <Button size="sm" variant="outline" asChild disabled={uploading}>
                  <span>
                    {uploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {t('workers.uploadFile')}
                  </span>
                </Button>
              </label>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FolderOpen className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('workers.noFiles')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.map(file => (
                  <div 
                    key={file.id} 
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 group"
                  >
                    {file.content_type?.startsWith('image/') ? (
                      <Image className="h-5 w-5 text-blue-500" />
                    ) : (
                      <FileText className="h-5 w-5 text-red-500" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB • {new Date(file.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => window.open(file.url, '_blank')}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteFile(file.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Main Workers Page
export default function WorkersPage() {
  const { api, hasRole } = useAuth();
  const { t } = useLanguage();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [profileWorker, setProfileWorker] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [editWorker, setEditWorker] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const fetchWorkers = async () => {
    try {
      // Fetch all workers including inactive
      const response = await api.get('/workers?include_inactive=true');
      setWorkers(response.data);
    } catch (err) {
      console.error('Failed to fetch workers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, []);

  const handleOpenProfile = (worker) => {
    setProfileWorker(worker);
    setProfileOpen(true);
  };

  const handleEditWorker = (worker) => {
    setEditWorker(worker);
    setEditOpen(true);
  };

  const canManage = hasRole('admin') || hasRole('manager');
  const canDelete = hasRole('admin');
  const currentUserRole = hasRole('admin') ? 'admin' : hasRole('manager') ? 'manager' : 'worker';

  // Filter workers by search and active status
  const filteredWorkers = workers.filter(w => {
    // Managers don't see admins in the list
    if (currentUserRole === 'manager' && w.role === 'admin') return false;

    // Filter by active/inactive tab
    const isActive = w.is_active !== false;
    if (activeTab === 'active' && !isActive) return false;
    if (activeTab === 'inactive' && isActive) return false;

    // Filter by search query
    const query = searchQuery.toLowerCase();
    return (
      w.first_name?.toLowerCase().includes(query) ||
      w.last_name?.toLowerCase().includes(query) ||
      w.email?.toLowerCase().includes(query) ||
      w.phone?.includes(query)
    );
  });

  const activeCount = workers.filter(w => w.is_active !== false).length;
  const inactiveCount = workers.filter(w => w.is_active === false).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('workers.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('workers.subtitle')}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setDialogOpen(true)} data-testid="add-worker-btn">
            <Plus className="mr-2 h-4 w-4" />
            {t('workers.addWorker')}
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('workers.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="workers-search"
        />
      </div>

      {/* Tabs: Active / Inactive */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">
            <UserCheck className="h-4 w-4 mr-2" />
            {t('workers.active')} ({activeCount})
          </TabsTrigger>
          <TabsTrigger value="inactive" data-testid="tab-inactive">
            <UserX className="h-4 w-4 mr-2" />
            {t('workers.inactive')} ({inactiveCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {/* Workers List */}
          <Card className="cgc-card overflow-hidden">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-accent" />
                </div>
              ) : filteredWorkers.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <User className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>
                    {searchQuery 
                      ? t('clients.notFound') 
                      : activeTab === 'active' 
                        ? t('workers.noWorkers')
                        : t('workers.noInactiveWorkers')
                    }
                  </p>
                  {canManage && !searchQuery && activeTab === 'active' && (
                    <Button 
                      variant="link" 
                      onClick={() => setDialogOpen(true)}
                      className="mt-2"
                    >
                      {t('workers.addFirst')}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredWorkers.map((worker) => (
                <div 
                  key={worker.id} 
                  className="flex items-center gap-3 p-4"
                  data-testid={`worker-row-${worker.id}`}
                >
                  {/* Avatar - click to open profile */}
                  <Avatar 
                    className="shrink-0 cursor-pointer hover:ring-2 hover:ring-accent transition-all"
                    onClick={() => handleOpenProfile(worker)}
                    data-testid={`worker-avatar-${worker.id}`}
                  >
                    <AvatarFallback 
                      style={{ backgroundColor: worker.color || '#3b82f6' }}
                      className="text-white"
                    >
                      {worker.first_name?.[0]}{worker.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Name & Phone */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium truncate">
                        {worker.first_name} {worker.last_name}
                      </p>
                      {worker.role === 'admin' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 whitespace-nowrap">
                          {t('workers.roleAdmin')}
                        </span>
                      )}
                      {worker.role === 'manager' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 whitespace-nowrap">
                          {t('workers.roleManager')}
                        </span>
                      )}
                      {(!worker.role || worker.role === 'worker') && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 whitespace-nowrap">
                          {t('workers.roleWorker')}
                        </span>
                      )}
                    </div>
                    {worker.phone && (
                      <p className="text-sm text-accent flex items-center gap-1">
                        <Phone className="h-3 w-3 shrink-0" />
                        <span className="truncate">{worker.phone}</span>
                      </p>
                    )}
                  </div>
                  
                  {/* Rate - hidden on very small screens */}
                  <div className="hidden xs:block text-sm shrink-0">
                    {worker.hourly_rate ? (
                      <span className={parseFloat(worker.hourly_rate) < 17.40 ? 'text-amber-500' : 'text-muted-foreground'}>
                        {parseFloat(worker.hourly_rate) < 17.40 && '⚠️ '}${worker.hourly_rate}/ч
                      </span>
                    ) : '—'}
                  </div>
                  
                  {/* Status + Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                      worker.is_active 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {worker.is_active ? t('workers.active') : t('workers.inactive')}
                    </span>
                    {(canDelete || (canManage && worker.role === 'worker')) && (
                      <WorkerActions
                        worker={worker}
                        onUpdate={fetchWorkers}
                        onOpenProfile={handleOpenProfile}
                        onEdit={handleEditWorker}
                        currentUserRole={currentUserRole}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Worker Dialog */}
      <AddWorkerDialog 
        open={dialogOpen} 
        onOpenChange={setDialogOpen}
        onSuccess={fetchWorkers}
      />

      {/* Worker Profile Sheet */}
      <WorkerProfileSheet
        worker={profileWorker}
        open={profileOpen}
        onOpenChange={setProfileOpen}
        onUpdate={fetchWorkers}
      />

      {/* Edit Worker Dialog */}
      <EditWorkerDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        worker={editWorker}
        onSuccess={fetchWorkers}
      />
    </div>
  );
}
